import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentPage(1);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredComics =
    slug === 'all'
      ? [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      : comics.filter((c) => c.categorySlug === slug);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 韩漫网`,
    description: `韩漫网${activeCategory.name}分类，收录${filteredComics.length}部韩国漫画作品，免费在线阅读`,
    url: `https://hanmanwang.com/category/${slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <main className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/all' },
              { label: activeCategory.name },
            ]}
          />
        </div>

        <section className="bg-background-50">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 pb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {activeCategory.name}
              {slug !== 'all' && '漫画'}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              共 {filteredComics.length} 部作品
            </p>

            <div className="flex flex-wrap gap-2 md:gap-3 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {paginatedComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer animate-scaleIn"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                        comic.status === '连载中'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center justify-between text-background-50 text-xs">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                          {(comic.totalViews / 10000).toFixed(0)}万
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-accent-500"></i></span>
                          {comic.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500">
                    {comic.totalChapters}话 · {comic.author}
                  </p>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                      page === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}